// Frontend Langfuse proxy client - uses backend API with secret key
export class LangfuseProxy {
  private baseUrl: string;

  constructor() {
    this.baseUrl = '/api/langfuse/trace';
  }

  async createTrace(params: {
    traceId: string;
    name?: string;  // Make name optional
    input?: any;
    metadata?: Record<string, any>;
    sessionId?: string;
    userId?: string;
  }) {
    console.log('Creating trace via proxy:', params.traceId);
    
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'create_trace',
        data: params
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to create trace: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Trace created via proxy:', result);
    return result;
  }

  async createSpan(params: {
    traceId: string;
    spanName: string;
    input?: any;
    metadata?: Record<string, any>;
  }) {
    console.log('Creating span via proxy:', params.spanName);
    
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'create_span',
        data: params
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to create span: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Span created via proxy:', result);
    return result;
  }

  async endSpan(params: {
    traceId: string;
    spanName: string;
    output?: any;
    metadata?: Record<string, any>;
  }) {
    console.log('Ending span via proxy:', params.spanName);
    
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'end_span',
        data: params
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to end span: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Span ended via proxy:', result);
    return result;
  }

  async createGeneration(params: {
    traceId: string;
    name: string;
    model?: string;
    input?: any;
    metadata?: Record<string, any>;
  }) {
    console.log('Creating generation via proxy:', params.name);
    
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'create_generation',
        data: params
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to create generation: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Generation created via proxy:', result);
    return result;
  }

  async flush() {
    console.log('Flushing events via proxy');
    
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'flush',
        data: {}
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to flush: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Events flushed via proxy:', result);
    return result;
  }

  async healthCheck() {
    const response = await fetch(this.baseUrl, {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error(`Health check failed: ${response.statusText}`);
    }

    return await response.json();
  }
}

// Export a singleton instance
export const langfuseProxy = new LangfuseProxy();

// Test function using the proxy
export async function testLangfuseProxy() {
  console.log('\n=== Testing Langfuse Proxy ===');
  
  try {
    // Health check first
    const health = await langfuseProxy.healthCheck();
    console.log('Health check:', health);
    
    if (!health.langfuse_configured) {
      console.error('❌ Langfuse not properly configured on backend');
      return false;
    }
    
    // Create a test trace
    const testTraceId = `proxy-test-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    
    await langfuseProxy.createTrace({
      traceId: testTraceId,
      name: 'proxy-test-trace',
      input: { message: 'Testing proxy integration' },
      metadata: {
        source: 'proxy-test',
        timestamp: new Date().toISOString()
      },
      sessionId: `proxy-session-${Date.now()}`,
      userId: 'proxy-test-user'
    });
    
    // Create a span
    await langfuseProxy.createSpan({
      traceId: testTraceId,
      spanName: 'test-span',
      input: { action: 'testing proxy' },
      metadata: {
        step: 'span-creation'
      }
    });
    
    // End the span
    await langfuseProxy.endSpan({
      traceId: testTraceId,
      spanName: 'test-span',
      output: { result: 'success' },
      metadata: {
        duration: '50ms'
      }
    });
    
    // Flush events
    await langfuseProxy.flush();
    
    console.log('✅ Proxy test successful! Check dashboard for trace:', testTraceId);
    return true;
    
  } catch (error) {
    console.error('❌ Proxy test failed:', error);
    return false;
  }
}